import { useMemo } from 'react';

import { Flex } from '@chakra-ui/react';
import {
  ListItemComp,
  ListItemCompContent,
  ListItemCompDescription,
  ListItemCompTitle,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  ListItemCompLeading,
  Icon,
  CircleChevronRight as CircleChevronRightIcon,
} from '@mesh.ai/rabbit';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import {
  API_EVENTS_TO_FE_EVENTS_MAPPING,
  NOTIFICATION_EVENTS_META,
  EVENTS_CATEGORY_MAP,
} from 'screens/admin/Notifications/Constants/NotificationSettingsConstants';

import { toggleNotificationEventChannel } from 'services/admin';
import { showSuccessSnackbar } from 'services/snackbar';

import { PaperWithShadow } from 'uiToolkitV1/Paper';

import { CATEGORIZED_NOTIFICATION_EVENTS } from '../constants/NotificationSettingsConstants';
import type { ChannelDetails, ChannelEventDetails, EventsDetailMap } from '../types';
import { NotificationEventTable } from './NotificationEventTable';

const convertEventsFormat = (eventsFromAPI: ChannelEventDetails[]) => {
  let extractedEvents: EventsDetailMap = {};
  eventsFromAPI.forEach((event) => {
    extractedEvents = { ...extractedEvents, ...(event.eventsDetailMap || {}) };
  });

  const moduleToEventMap: Record<
    string,
    { moduleType: string; moduleOrder: number; eventsDetailMap: EventsDetailMap }
  > = {};

  Object.keys(extractedEvents).forEach((eventKey) => {
    if (API_EVENTS_TO_FE_EVENTS_MAPPING[eventKey]) {
      const eventDetails = EVENTS_CATEGORY_MAP[API_EVENTS_TO_FE_EVENTS_MAPPING[eventKey]];
      // if evenDetails present in FE mapping
      if (eventDetails) {
        if (moduleToEventMap[eventDetails.category]) {
          moduleToEventMap[eventDetails.category].eventsDetailMap[API_EVENTS_TO_FE_EVENTS_MAPPING[eventKey]] =
            extractedEvents[eventKey];
        } else {
          moduleToEventMap[eventDetails.category] = {
            moduleType: eventDetails.category,
            moduleOrder: NOTIFICATION_EVENTS_META?.[eventDetails?.category]?.order ?? 0,
            eventsDetailMap: {
              [API_EVENTS_TO_FE_EVENTS_MAPPING[eventKey]]: extractedEvents[eventKey],
            },
          };
        }
      }
    }
  });

  const sortedModuleEventArray = Object.values(moduleToEventMap).sort(
    (eventModule1, eventModule2) => eventModule1.moduleOrder - eventModule2.moduleOrder
  );

  const sortedModuleToEventMap: Record<
    string,
    { moduleType: string; moduleOrder: number; eventsDetailMap: EventsDetailMap }
  > = {};
  sortedModuleEventArray.forEach((eventModule) => {
    sortedModuleToEventMap[eventModule.moduleType] = eventModule;
  });

  return sortedModuleToEventMap;
};

type NotificationEventProps = {
  isEmployeeNotificationSettings?: boolean;
  channelDetailsArray: ChannelDetails[];
  channelEventDetailsArray: ChannelEventDetails[];
  refreshData: () => void;
};

export const NotificationEvent = ({
  isEmployeeNotificationSettings = false,
  channelDetailsArray,
  channelEventDetailsArray,
  refreshData,
}: NotificationEventProps) => {
  const dispatch = useDispatch();
  const toast = useToast();

  const channelDetailsMap = useMemo(
    () => channelDetailsArray.reduce((a, v) => ({ ...a, [v.channelType]: { ...v } }), {}),
    [channelDetailsArray]
  );

  const eventChannelOnToggle = ({ moduleType, mainEventType, channelType, channelState }) => {
    const mainEventTypesMap = Object.keys(API_EVENTS_TO_FE_EVENTS_MAPPING);

    const mainEventTypeAPI = mainEventTypesMap.find(
      (apiEventName) => API_EVENTS_TO_FE_EVENTS_MAPPING[apiEventName] === mainEventType
    );

    if (mainEventTypeAPI === undefined) {
      toast({
        title: 'Unknown notification event error',
        status: 'error',
      });
      return;
    }

    toggleNotificationEventChannel({
      moduleType,
      mainEventType,
      channelType,
      channelState,
      isEmployeeNotificationSettings,
    })
      .then(() => {
        refreshData();
        // @ts-expect-error ts issue
        dispatch(showSuccessSnackbar('Notification settings saved successfully!'));
      })
      .catch((err) => {
        console.error('Error in toggling the channel and event : ', err);
      });
  };

  const formattedChannelEventDetailsMap = useMemo(
    () => convertEventsFormat(channelEventDetailsArray),
    [channelEventDetailsArray]
  );

  // filter out the events that are not present in formattedChannelEventDetailsMap
  const filteredCategorizedNotificationEvents = useMemo(
    () =>
      CATEGORIZED_NOTIFICATION_EVENTS.filter(({ events }) =>
        events.some((event) => formattedChannelEventDetailsMap[event])
      ),
    [formattedChannelEventDetailsMap]
  );

  return (
    <PaperWithShadow p={3} mt={3}>
      <ListItemComp style={{ padding: '0 0 8px 8px', height: '44px', borderBottom: '1px solid #F5F7F9' }}>
        <ListItemCompContent>
          <ListItemCompTitle>What you get notified about</ListItemCompTitle>
          <ListItemCompDescription>
            {!isEmployeeNotificationSettings
              ? "Choose how you'd like your people to be notified across each module"
              : "Choose how you'd like to be notified across each module"}
          </ListItemCompDescription>
        </ListItemCompContent>
      </ListItemComp>
      <Accordion collapsible type="single">
        {filteredCategorizedNotificationEvents.map(({ name, description, icon, events }, index) => (
          <AccordionItem
            value={name}
            key={name}
            style={{
              borderBottom: index === filteredCategorizedNotificationEvents.length - 1 ? 'none' : '1px solid #F5F7F9',
            }}
          >
            <AccordionTrigger icon={CircleChevronRightIcon} style={{ color: '#0D819F' }}>
              <ListItemComp style={{ paddingLeft: '4px' }}>
                <ListItemCompLeading style={{ color: '#666666' }}>
                  <Icon IconSVG={icon} />
                </ListItemCompLeading>
                <ListItemCompContent style={{ textAlign: 'left' }}>
                  <ListItemCompTitle bold={false}>{name}</ListItemCompTitle>
                  <ListItemCompDescription>{description}</ListItemCompDescription>
                </ListItemCompContent>
              </ListItemComp>
            </AccordionTrigger>
            <AccordionContent>
              <Flex flexDirection="column" gap="12px">
                {Object.keys(formattedChannelEventDetailsMap).length > 0 &&
                  events
                    .filter((event) => formattedChannelEventDetailsMap[event]) // filter out the events that are not present in formattedChannelEventDetailsMap
                    .map((event) => {
                      const notificationEvent = formattedChannelEventDetailsMap[event];
                      // @ts-expect-error featureFlag doesn't exist in notificationEvent but leaving it here just in case
                      const { moduleType, eventsDetailMap, featureFlag } = notificationEvent;

                      if (!featureFlag || (featureFlag && featureFlag())) {
                        return (
                          <NotificationEventTable
                            key={moduleType}
                            type={moduleType}
                            eventsDetailMap={eventsDetailMap}
                            onToggle={eventChannelOnToggle}
                            channelDetailsMap={channelDetailsMap}
                            renderTableName={events.length !== 1}
                          />
                        );
                      }

                      return null;
                    })}
              </Flex>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </PaperWithShadow>
  );
};
